if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handlePrevClick = this.handlePrevClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleThumbClick = this.handleThumbClick.bind(this);
      }

      show(opener) {
        super.show(opener);

        this.cacheElements();
        this.unbindEvents();
        this.bindEvents();
        this.showActiveMedia();
      }

      hide() {
        this.unbindEvents();
        window.pauseAllMedia?.();
        super.hide();
      }

      cacheElements() {
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.panel = this.querySelector('.product-media-modal__panel');
        this.container = this.querySelector('[data-product-modal-content]');
        this.closeButton = this.querySelector('.product-media-modal__toggle');
        this.prevButton = this.querySelector('[data-product-modal-prev]');
        this.nextButton = this.querySelector('[data-product-modal-next]');
        this.thumbButtons = Array.from(this.querySelectorAll('[data-product-modal-thumb]'));
      }

      bindEvents() {
        document.addEventListener('keydown', this.handleKeydown);

        this.dialog?.addEventListener('click', this.handleBackdropClick);
        this.closeButton?.addEventListener('click', this.handleCloseClick);
        this.prevButton?.addEventListener('click', this.handlePrevClick);
        this.nextButton?.addEventListener('click', this.handleNextClick);

        this.thumbButtons.forEach((button) => {
          button.addEventListener('click', this.handleThumbClick);
        });
      }

      unbindEvents() {
        document.removeEventListener('keydown', this.handleKeydown);

        this.dialog?.removeEventListener('click', this.handleBackdropClick);
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        this.prevButton?.removeEventListener('click', this.handlePrevClick);
        this.nextButton?.removeEventListener('click', this.handleNextClick);

        if (this.thumbButtons?.length) {
          this.thumbButtons.forEach((button) => {
            button.removeEventListener('click', this.handleThumbClick);
          });
        }
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.hide();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goToPrevious();
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goToNext();
        }
      }

      handleBackdropClick(event) {
        if (this.panel && !this.panel.contains(event.target)) {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        }
      }

      handleCloseClick(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.hide();
      }

      handlePrevClick(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.goToPrevious();
      }

      handleNextClick(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.goToNext();
      }

      handleThumbClick(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const mediaId = event.currentTarget.getAttribute('data-media-id');
        if (mediaId) {
          this.setActiveMediaById(mediaId);
        }
      }

      getContainer() {
        return this.container || this.querySelector('[data-product-modal-content]');
      }

      getMediaItems() {
        const container = this.getContainer();
        if (!container) return [];

        return Array.from(container.children).filter((item) => item.hasAttribute('data-media-id'));
      }

      getThumbButtons() {
        return this.thumbButtons?.length
          ? this.thumbButtons
          : Array.from(this.querySelectorAll('[data-product-modal-thumb]'));
      }

      getActiveMedia() {
        return this.getMediaItems().find((item) => item.classList.contains('active')) || null;
      }

      getActiveIndex() {
        const items = this.getMediaItems();
        const active = this.getActiveMedia();

        return Math.max(
          0,
          items.findIndex((item) => item === active)
        );
      }

      updateThumbState(activeMediaId) {
        this.getThumbButtons().forEach((button) => {
          button.classList.toggle(
            'is-active',
            button.getAttribute('data-media-id') === activeMediaId
          );
        });

        const activeThumb = this.querySelector(
          `[data-product-modal-thumb][data-media-id="${activeMediaId}"]`
        );

        if (activeThumb) {
          activeThumb.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }

      updateNavState() {
        const items = this.getMediaItems();
        const hasMultiple = items.length > 1;

        if (this.prevButton) this.prevButton.disabled = !hasMultiple;
        if (this.nextButton) this.nextButton.disabled = !hasMultiple;
      }

      resolveMediaId(mediaId) {
        const items = this.getMediaItems();
        if (!items.length) return null;

        const firstId = items[0].getAttribute('data-media-id');
        if (!mediaId) return firstId;

        const exactMatch = items.find((item) => item.getAttribute('data-media-id') === mediaId);
        if (exactMatch) return mediaId;

        const modalSectionId = this.id.replace('ProductModal-', '');
        const prefixedId = `${modalSectionId}-${mediaId}`;
        const prefixedMatch = items.find((item) => item.getAttribute('data-media-id') === prefixedId);
        if (prefixedMatch) return prefixedId;

        const suffixMatch = items.find((item) => item.getAttribute('data-media-id').endsWith(`-${mediaId}`));
        if (suffixMatch) return suffixMatch.getAttribute('data-media-id');

        return firstId;
      }

      resetStageScroll() {
        const container = this.getContainer();
        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }

        const active = this.getActiveMedia();
        if (active) {
          active.scrollTop = 0;
          active.scrollLeft = 0;
        }

        if (this.panel) {
          this.panel.scrollTop = 0;
          this.panel.scrollLeft = 0;
        }
      }

      setActiveMediaById(mediaId) {
        const items = this.getMediaItems();
        if (!items.length) return;

        const resolvedMediaId = this.resolveMediaId(mediaId);
        if (!resolvedMediaId) return;

        const activeMedia = items.find((item) => item.getAttribute('data-media-id') === resolvedMediaId);
        if (!activeMedia) return;

        items.forEach((item) => item.classList.remove('active'));
        activeMedia.classList.add('active');

        this.resetStageScroll();
        this.updateThumbState(resolvedMediaId);
        this.updateNavState();
        this.playActiveMedia(activeMedia);
      }

      goToPrevious() {
        const items = this.getMediaItems();
        if (items.length <= 1) return;

        const currentIndex = this.getActiveIndex();
        const nextIndex = (currentIndex - 1 + items.length) % items.length;
        const nextMediaId = items[nextIndex].getAttribute('data-media-id');

        if (nextMediaId) {
          this.setActiveMediaById(nextMediaId);
        }
      }

      goToNext() {
        const items = this.getMediaItems();
        if (items.length <= 1) return;

        const currentIndex = this.getActiveIndex();
        const nextIndex = (currentIndex + 1) % items.length;
        const nextMediaId = items[nextIndex].getAttribute('data-media-id');

        if (nextMediaId) {
          this.setActiveMediaById(nextMediaId);
        }
      }

      playActiveMedia(activeMedia) {
        window.pauseAllMedia?.();

        const deferredMedia = activeMedia.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          typeof activeMedia.loadContent === 'function'
        ) {
          activeMedia.loadContent();
        }
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        this.setActiveMediaById(mediaId);
      }
    }
  );
}